import { useEffect, useRef, useState } from "react";
import { FaCheck, FaChevronDown } from "react-icons/fa";

export default function GalleryEventSelect({ options, value, onChange, id = "gallery-event-select", labelId = "gallery-event-label" }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    function closeOutside(event) {
      if (!rootRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  function select(option) {
    onChange(option);
    setOpen(false);
    triggerRef.current.focus();
  }

  return (
    <div className="gallery-event-picker" ref={rootRef} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <button
        ref={triggerRef}
        id={id}
        className="gallery-event-picker-trigger"
        type="button"
        aria-labelledby={`${labelId} ${id}-value`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-options`}
        onClick={() => {
          setActiveIndex(Math.max(0, options.indexOf(value)));
          setOpen((previous) => !previous);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(Math.max(0, options.indexOf(value)));
            setOpen(true);
          }
        }}
      >
        <span id={`${id}-value`}>{value}</span>
        <FaChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div id={`${id}-options`} className="gallery-event-picker-menu" role="listbox" aria-labelledby={labelId}
          onKeyDown={(event) => {
            if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
              event.preventDefault();
              setActiveIndex((index) => event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 :
                (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
            } else if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
              triggerRef.current.focus();
            } else if (event.key.length === 1 && /\S/.test(event.key)) {
              const index = options.findIndex((option) => option.toLowerCase().startsWith(event.key.toLowerCase()));
              if (index >= 0) setActiveIndex(index);
            }
          }}>
          {options.map((option, index) => (
            <button
              ref={(element) => { optionRefs.current[index] = element; }}
              className="gallery-event-picker-option"
              key={option}
              type="button"
              role="option"
              aria-selected={value === option}
              tabIndex={index === activeIndex ? 0 : -1}
              onFocus={() => setActiveIndex(index)}
              onClick={() => select(option)}
            >
              <span>{option}</span>
              {value === option && <FaCheck aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
